import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calculator, FileText } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINES = ["Få oversikt.", "Du har rettigheter.", "Det finnes en vei videre."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden grain-overlay pt-32 pb-20 lg:pt-44 lg:pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span data-testid="hero-badge" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-mono font-semibold text-primary bg-accent px-4 py-2 rounded-full">
              Uavhengig opplysningsportal
            </span>
          </motion.div>

          <h1 data-testid="hero-heading" className="mt-8 font-serif font-bold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 ? <span className="italic text-terracotta">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtext"
            className="mt-7 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
          >
            Sliter du med regninger, inkasso eller gjeld? Her finner du samlede lenker til
            offentlige sider, søknadsskjemaer, gratis rådgivning — og enkle verktøy som hjelper
            deg å få oversikt over økonomien din.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          >
            <button data-testid="hero-btn-calculator" onClick={() => scrollToId("budsjett")}
              className="inline-flex items-center gap-2.5 bg-primary text-primary-foreground px-7 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25">
              <Calculator size={16} /> Lag ditt budsjett
            </button>
            <button data-testid="hero-btn-resources" onClick={() => scrollToId("ressurser")}
              className="inline-flex items-center gap-2.5 border border-foreground/20 px-7 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 hover:border-foreground hover:-translate-y-0.5">
              <FileText size={16} /> Offentlige skjemaer
            </button>
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5 relative"
          style={{ y }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative rounded-[2rem] overflow-hidden border border-border shadow-2xl shadow-primary/10 aspect-[4/5] max-w-md mx-auto">
            <img
              src="/hero.jpg"
              alt="To personer som går gjennom regninger og økonomi sammen ved kjøkkenbordet"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-surface border border-border rounded-2xl px-5 py-4 shadow-xl shadow-primary/10 hidden sm:block">
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Gratis hjelpetelefon</p>
            <p className="font-serif font-bold text-xl mt-1">NAV 55 55 33 33</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
